/**
 * Reading a supplier's invoice (a cost Braynewave received) from its PDF (R189; docs/specs/cloud/platform-costs.md
 * §4): the values of the cost form, Claude's prompt and JSON schema, the normalising of Claude's answer, and the
 * text-layer heuristics used when the platform's Claude key is not set (or Claude does not answer). The admin checks
 * every value before anything is saved.
 *
 * This file has no imports on purpose: the Edge Function `platform-costs` (Deno) uses an identical copy,
 * `supabase/functions/_shared/supplier-invoice-reading.ts` (`node tools/dev/copy-edge-shared.mjs` writes it, and its
 * `--check` fails while the two differ). Edit this file, then run the tool.
 */

/** The Edge Function that reads a supplier's invoice and stores the platform's Claude key. */
export const PLATFORM_COSTS_FUNCTION = 'platform-costs';
/** The model the function asks (the claude-api skill's current default). */
export const SUPPLIER_INVOICE_READ_MODEL = 'claude-opus-5-5';
/** The largest PDF read and kept (the bucket's limit too). */
export const SUPPLIER_INVOICE_PDF_MAX_BYTES = 10 * 1024 * 1024;
/** The platform key's provider (one row of `platform_integration`). */
export const PLATFORM_KEY_PROVIDER = 'anthropic';

// ------------------------------------------------------------------------------------------------ values

export interface SupplierInvoiceValues {
  readonly supplier: string | null;
  /** 11 digits, no spaces. */
  readonly supplier_abn: string | null;
  readonly invoice_number: string | null;
  /** ISO dates. */
  readonly invoice_date: string | null;
  readonly due_date: string | null;
  readonly paid_on: string | null;
  readonly description: string | null;
  /** Three capital letters (AUD, USD). */
  readonly currency: string | null;
  readonly amount_ex_gst: number | null;
  readonly gst: number | null;
  readonly total: number | null;
}

export type SupplierInvoiceField = keyof SupplierInvoiceValues;

export const SUPPLIER_INVOICE_FIELDS: readonly SupplierInvoiceField[] = [
  'supplier',
  'supplier_abn',
  'invoice_number',
  'invoice_date',
  'due_date',
  'paid_on',
  'description',
  'currency',
  'amount_ex_gst',
  'gst',
  'total',
];

/** The words of each box, as the cost form labels them. */
export const SUPPLIER_INVOICE_FIELD_LABEL: Readonly<Record<SupplierInvoiceField, string>> = {
  supplier: 'Supplier',
  supplier_abn: 'Supplier ABN',
  invoice_number: 'Invoice number',
  invoice_date: 'Invoice date',
  due_date: 'Due date',
  paid_on: 'Paid on',
  description: 'Description',
  currency: 'Currency',
  amount_ex_gst: 'Amount ex GST',
  gst: 'GST',
  total: 'Total',
};

const MONEY_FIELDS: readonly SupplierInvoiceField[] = ['amount_ex_gst', 'gst', 'total'];

export function emptySupplierInvoice(): SupplierInvoiceValues {
  return {
    supplier: null,
    supplier_abn: null,
    invoice_number: null,
    invoice_date: null,
    due_date: null,
    paid_on: null,
    description: null,
    currency: null,
    amount_ex_gst: null,
    gst: null,
    total: null,
  };
}

// ------------------------------------------------------------------------------------------------ parsing

/** The digits of an ABN as typed or printed (`82 895 579 345`), or null when it is not 11 digits. */
export function abnDigits(v: unknown): string | null {
  if (typeof v !== 'string' && typeof v !== 'number') return null;
  const d = String(v).replace(/[\s.-]/g, '');
  return /^\d{11}$/.test(d) ? d : null;
}

/** The ABN check (the ATO's: 1 off the first digit, weights 10, 1, 3, 5 … 19, the sum divisible by 89). */
export function abnValid(v: unknown): boolean {
  const d = abnDigits(v);
  if (!d) return false;
  const w = [10, 1, 3, 5, 7, 9, 11, 13, 15, 17, 19];
  let sum = 0;
  for (let i = 0; i < 11; i += 1) sum += (Number(d[i]) - (i === 0 ? 1 : 0)) * w[i]!;
  return sum % 89 === 0;
}

/** `82895579345` as `82 895 579 345`. */
export function formatAbn(v: string | null): string {
  const d = abnDigits(v);
  return d ? `${d.slice(0, 2)} ${d.slice(2, 5)} ${d.slice(5, 8)} ${d.slice(8)}` : (v ?? '');
}

const pad = (n: number): string => String(n).padStart(2, '0');

function validYmd(y: number, m: number, d: number): string | null {
  if (!(y >= 1990 && y <= 2199 && m >= 1 && m <= 12 && d >= 1 && d <= 31)) return null;
  const t = new Date(Date.UTC(y, m - 1, d));
  if (t.getUTCFullYear() !== y || t.getUTCMonth() !== m - 1 || t.getUTCDate() !== d) return null;
  return `${y}-${pad(m)}-${pad(d)}`;
}

const MONTHS: Readonly<Record<string, number>> = {
  jan: 1,
  feb: 2,
  mar: 3,
  apr: 4,
  may: 5,
  jun: 6,
  jul: 7,
  aug: 8,
  sep: 9,
  oct: 10,
  nov: 11,
  dec: 12,
};
const monthOf = (s: string): number | null => MONTHS[s.toLowerCase().slice(0, 3)] ?? null;
const MONTH_WORD =
  '(Jan(?:uary)?|Feb(?:ruary)?|Mar(?:ch)?|Apr(?:il)?|May|June?|July?|Aug(?:ust)?|Sep(?:t(?:ember)?)?|Oct(?:ober)?|Nov(?:ember)?|Dec(?:ember)?)';
const yearOf = (s: string): number => (s.length === 2 ? 2000 + Number(s) : Number(s));

/** The date patterns an invoice prints, each with how to read its groups. */
const DATE_PATTERNS: ReadonlyArray<readonly [RegExp, (m: RegExpExecArray) => string | null]> = [
  [/\b(\d{4})-(\d{1,2})-(\d{1,2})\b/, (m) => validYmd(Number(m[1]), Number(m[2]), Number(m[3]))],
  // 3 November 2026, 3 Nov 2026, 03-Nov-2026, 3rd November 2026
  [
    new RegExp(`\\b(\\d{1,2})(?:st|nd|rd|th)?[\\s-]+${MONTH_WORD}\\.?,?[\\s-]+(\\d{4}|\\d{2})\\b`, 'i'),
    (m) => {
      const mo = monthOf(m[2]!);
      return mo ? validYmd(yearOf(m[3]!), mo, Number(m[1])) : null;
    },
  ],
  // November 3, 2026 (the American order, which Stripe's invoices print)
  [
    new RegExp(`\\b${MONTH_WORD}\\.?\\s+(\\d{1,2})(?:st|nd|rd|th)?,?\\s+(\\d{4})\\b`, 'i'),
    (m) => {
      const mo = monthOf(m[1]!);
      return mo ? validYmd(Number(m[3]), mo, Number(m[2])) : null;
    },
  ],
  // 03/11/2026, 3.11.26: day first (Australia)
  [/\b(\d{1,2})[/.](\d{1,2})[/.](\d{4}|\d{2})\b/, (m) => validYmd(yearOf(m[3]!), Number(m[2]), Number(m[1]))],
];

/** The first date in some text as an ISO string, or null. */
export function findInvoiceDate(s: string): string | null {
  let best: { at: number; iso: string } | null = null;
  for (const [re, read] of DATE_PATTERNS) {
    const m = re.exec(s);
    if (!m) continue;
    const iso = read(m);
    if (iso && (best === null || m.index < best.at)) best = { at: m.index, iso };
  }
  return best?.iso ?? null;
}

/** A date as given (ISO, or as an invoice prints it) as ISO, or null. */
export function parseInvoiceDate(v: unknown): string | null {
  if (typeof v !== 'string') return null;
  return findInvoiceDate(v.trim());
}

/** An amount of money: `1,253.32`, `$1,253.32`, `US$12.00`, `(15.00)` or `-15.00` (a credit), a number as it is. */
export function parseMoney(v: unknown): number | null {
  let n: number | null = null;
  if (typeof v === 'number') n = v;
  else if (typeof v === 'string') {
    let s = v.trim().replace(/\b(AUD|USD|NZD|EUR|GBP|CAD|SGD)\b/gi, '');
    s = s.replace(/(A|AU|US|NZ|C|S)?\$/gi, '').replace(/\s+/g, '');
    let negative = false;
    if (/^\(.*\)$/.test(s)) {
      negative = true;
      s = s.slice(1, -1);
    }
    if (/CR$/i.test(s)) {
      negative = true;
      s = s.slice(0, -2);
    }
    if (s.startsWith('-') || s.startsWith('−')) {
      negative = !negative;
      s = s.slice(1);
    }
    if (/^\d{1,3}(,\d{3})*(\.\d{1,2})?$|^\d+(\.\d{1,2})?$/.test(s))
      n = Number(s.replace(/,/g, '')) * (negative ? -1 : 1);
  }
  if (n === null || !Number.isFinite(n) || Math.abs(n) > 10_000_000) return null;
  return Math.round(n * 100) / 100;
}

/** A currency as given: `AUD`, `aud`, `A$`, `US$`, `USD`; null when not one (a bare `$` says nothing). */
export function parseCurrency(v: unknown): string | null {
  if (typeof v !== 'string') return null;
  const s = v.trim().toUpperCase();
  if (s === 'A$' || s === 'AU$') return 'AUD';
  if (s === 'US$') return 'USD';
  if (s === 'NZ$') return 'NZD';
  return /^[A-Z]{3}$/.test(s) ? s : null;
}

const text = (v: unknown, max: number): string | null => {
  if (typeof v !== 'string' && typeof v !== 'number') return null;
  const s = String(v).trim().replace(/\s+/g, ' ');
  return s === '' ? null : s.slice(0, max);
};

const asObject = (v: unknown): Record<string, unknown> =>
  v !== null && typeof v === 'object' && !Array.isArray(v) ? (v as Record<string, unknown>) : {};

/** Values (Claude's, the heuristics' or typed) in their checked shape: anything not readable becomes null. */
export function normaliseSupplierInvoiceValues(raw: unknown): SupplierInvoiceValues {
  const r = asObject(raw);
  const abn = abnDigits(r.supplier_abn);
  const number = text(r.invoice_number, 60);
  return {
    supplier: text(r.supplier, 120),
    supplier_abn: abn && abnValid(abn) ? abn : null,
    invoice_number: number && /^[\w./#-][\w ./#-]*$/.test(number) ? number : null,
    invoice_date: parseInvoiceDate(r.invoice_date),
    due_date: parseInvoiceDate(r.due_date),
    paid_on: parseInvoiceDate(r.paid_on),
    description: text(r.description, 500),
    currency: parseCurrency(r.currency),
    amount_ex_gst: parseMoney(r.amount_ex_gst),
    gst: parseMoney(r.gst),
    total: parseMoney(r.total),
  };
}

const cents = (n: number): number => Math.round(n * 100);

/**
 * The amounts completed from each other (two of ex GST, GST and total give the third), and whether they add up (to
 * the cent). Values already there are kept.
 */
export function completeAmounts(v: SupplierInvoiceValues): {
  values: SupplierInvoiceValues;
  addsUp: boolean;
} {
  let { amount_ex_gst: ex, gst, total } = v;
  if (ex === null && gst !== null && total !== null) ex = (cents(total) - cents(gst)) / 100;
  if (total === null && ex !== null && gst !== null) total = (cents(ex) + cents(gst)) / 100;
  if (gst === null && ex !== null && total !== null) gst = (cents(total) - cents(ex)) / 100;
  const addsUp = ex === null || gst === null || total === null || cents(ex) + cents(gst) === cents(total);
  return { values: { ...v, amount_ex_gst: ex, gst, total }, addsUp };
}

/** A reading as the function answers it: the values, and the boxes to check closely. */
export interface SupplierInvoiceReading {
  readonly values: SupplierInvoiceValues;
  readonly unsure: readonly SupplierInvoiceField[];
}

const isField = (x: unknown): x is SupplierInvoiceField =>
  typeof x === 'string' && (SUPPLIER_INVOICE_FIELDS as readonly string[]).includes(x);

/**
 * Claude's answer as a reading: the values normalised and completed, and as unsure every box Claude named, every box
 * it filled that did not parse, an ABN that fails its check, and the total when the amounts do not add up.
 */
export function normaliseSupplierInvoice(raw: unknown): SupplierInvoiceReading {
  const r = asObject(raw);
  const given = asObject(r.values ?? r);
  const { values, addsUp } = completeAmounts(normaliseSupplierInvoiceValues(given));
  const unsure = new Set<SupplierInvoiceField>(Array.isArray(r.unsure) ? r.unsure.filter(isField) : []);
  for (const k of SUPPLIER_INVOICE_FIELDS) {
    const g = given[k];
    if (g !== null && g !== undefined && g !== '' && values[k] === null) unsure.add(k);
  }
  if (!addsUp) unsure.add('total');
  return { values, unsure: SUPPLIER_INVOICE_FIELDS.filter((k) => unsure.has(k)) };
}

// ------------------------------------------------------------------------------------------------ Claude

const nullable = (type: 'string' | 'number'): Record<string, unknown> => ({
  anyOf: [{ type }, { type: 'null' }],
});

/** The JSON schema Claude answers to: `{ values: {…}, unsure: [box names] }`. */
export function supplierInvoiceSchema(): Record<string, unknown> {
  const props = Object.fromEntries(
    SUPPLIER_INVOICE_FIELDS.map((k) => [k, nullable(MONEY_FIELDS.includes(k) ? 'number' : 'string')]),
  );
  const values = {
    type: 'object',
    properties: props,
    required: [...SUPPLIER_INVOICE_FIELDS],
    additionalProperties: false,
  };
  return {
    type: 'object',
    properties: {
      values,
      unsure: { type: 'array', items: { type: 'string', enum: [...SUPPLIER_INVOICE_FIELDS] } },
    },
    required: ['values', 'unsure'],
    additionalProperties: false,
  };
}

/** Claude's instructions (the system prompt). */
export function supplierInvoicePrompt(): string {
  return [
    'You read one invoice that a supplier sent to Braynewave (Michael Brayne, an Australian sole trader who runs the Teslyn software platform), so the owner can check the values beside the PDF before recording the cost.',
    'supplier is the business that issued the invoice (not Braynewave or Michael Brayne, who is the customer).',
    "supplier_abn is the supplier's Australian Business Number when printed (11 digits); never Braynewave's own ABN 82 895 579 345. Null when there is none (most overseas suppliers).",
    'invoice_number is the invoice or receipt number as printed.',
    'invoice_date is the date the invoice was issued; due_date the date payment is due; paid_on the date it was paid when the invoice or receipt says so. Answer dates as YYYY-MM-DD. Numeric dates on an Australian invoice are day/month/year; "November 3, 2026" is 2026-11-03.',
    'description says in a few words what was bought and for which period (for example "Pro plan, 1 Oct to 31 Oct 2026").',
    'currency is the three-letter code the amounts are in (AUD, USD).',
    'amount_ex_gst is the total before GST (or other tax), gst the GST or tax amount (0 when the invoice shows none), total the amount charged including tax. Answer amounts as numbers without symbols; a credit note is negative.',
    'Read only what is printed. Never guess: leave a box null when the invoice does not show it.',
    'List in "unsure" every box you could not read with confidence.',
    'Do not copy card numbers, bank account numbers or addresses.',
    'Answer only with the JSON object of the schema.',
  ].join('\n');
}

/** The request the function makes of Claude, provider-neutral (the function's index.ts builds the call). */
export interface SupplierInvoiceReadRequest {
  readonly model: string;
  readonly system: string;
  readonly schema: Record<string, unknown>;
  readonly mediaType: 'application/pdf';
  /** Base64, no line breaks. */
  readonly data: string;
}

export function supplierInvoiceReadRequest(
  data: string,
  model: string = SUPPLIER_INVOICE_READ_MODEL,
): SupplierInvoiceReadRequest {
  return {
    model,
    system: supplierInvoicePrompt(),
    schema: supplierInvoiceSchema(),
    mediaType: 'application/pdf',
    data,
  };
}

/** Why a PDF cannot be read, or null: the media type and the size of the decoded file. */
export function supplierInvoiceReadProblem(mediaType: unknown, base64Length: number): string | null {
  if (mediaType !== 'application/pdf') return 'An invoice is read from a PDF.';
  const bytes = Math.floor((base64Length * 3) / 4);
  if (bytes === 0) return 'The PDF is empty.';
  if (bytes > SUPPLIER_INVOICE_PDF_MAX_BYTES)
    return `The PDF is ${(bytes / 1024 / 1024).toFixed(1)} MB; the reader takes ${SUPPLIER_INVOICE_PDF_MAX_BYTES / 1024 / 1024} MB.`;
  return null;
}

// ------------------------------------------------------------------------------------------------ the text layer

/** One piece of text of a PDF page as pdf.js gives it (its position in PDF units, origin at the bottom left). */
export interface PdfTextPiece {
  readonly page: number;
  readonly x: number;
  readonly y: number;
  readonly str: string;
}

/** The pieces as lines of text, page by page, top to bottom, left to right (pieces within 2 units of height share a line). */
export function pdfTextLines(pieces: readonly PdfTextPiece[]): string[] {
  const sorted = pieces
    .filter((p) => p.str.trim() !== '')
    .slice()
    .sort((a, b) => a.page - b.page || b.y - a.y || a.x - b.x);
  const lines: { page: number; y: number; parts: PdfTextPiece[] }[] = [];
  for (const p of sorted) {
    const last = lines[lines.length - 1];
    if (last && last.page === p.page && Math.abs(last.y - p.y) <= 2) last.parts.push(p);
    else lines.push({ page: p.page, y: p.y, parts: [p] });
  }
  return lines.map((l) =>
    l.parts
      .sort((a, b) => a.x - b.x)
      // a glyph the font does not map comes through as a control character (Stripe's hyphen is a NUL): a space
      // eslint-disable-next-line no-control-regex
      .map((p) => p.str.replace(/[\u0000-\u001f\u007f]/g, ' ').trim())
      .join(' ')
      .replace(/\s+/g, ' ')
      .trim(),
  );
}

/** Suppliers Braynewave pays, with the category code each suggests (spec §3). */
export const KNOWN_SUPPLIERS: ReadonlyArray<{
  readonly match: RegExp;
  readonly name: string;
  readonly category: string;
}> = [
  { match: /\bsupabase\b/i, name: 'Supabase', category: 'hosting' },
  { match: /\bcloudflare\b/i, name: 'Cloudflare', category: 'cloudflare' },
  { match: /\b(powersync|journey ?apps)\b/i, name: 'PowerSync', category: 'powersync' },
  { match: /\bresend\b/i, name: 'Resend', category: 'resend' },
  // "Apple Pay" and "Google Pay" say how an invoice was paid, not who sent it
  { match: /\bapple\b(?!\s*pay)/i, name: 'Apple', category: 'apple' },
  { match: /\b(firebase|google)\b(?!\s*pay)/i, name: 'Google', category: 'google' },
  { match: /\b(expo|650 industries)\b/i, name: 'Expo', category: 'expo' },
  { match: /\bgithub\b/i, name: 'GitHub', category: 'github' },
  { match: /\banthropic\b/i, name: 'Anthropic', category: 'anthropic' },
  {
    match: /\b(ventraip|crazy domains|godaddy|namecheap|porkbun|netregistry|synergy wholesale)\b/i,
    name: '',
    category: 'domains',
  },
];

/** The category code a supplier's name (or the invoice's text) suggests, or null. */
export function suggestCategoryCode(supplier: string | null, body = ''): string | null {
  for (const s of KNOWN_SUPPLIERS) if (supplier && s.match.test(supplier)) return s.category;
  for (const s of KNOWN_SUPPLIERS) if (body && s.match.test(body)) return s.category;
  return null;
}

/** An amount on a line: two decimals, so dates, percentages and counts are not taken. */
const MONEY_RE =
  /(?:\b(?:AUD|USD|NZD)\s*)?(?:(?:A|AU|US|NZ)?\$\s*)?\(?-?\d{1,3}(?:,\d{3})*\.\d{2}\)?(?:\s*(?:AUD|USD|NZD)\b)?|(?:(?:A|AU|US|NZ)?\$\s*)?\(?-?\d+\.\d{2}\)?/gi;

function moneyOnLine(line: string): number[] {
  const out: number[] = [];
  for (const m of line.matchAll(MONEY_RE)) {
    // a percentage ("10.00%") is not money
    const after = line.slice((m.index ?? 0) + m[0].length);
    if (/^\s*%/.test(after)) continue;
    const n = parseMoney(m[0]);
    if (n !== null) out.push(n);
  }
  return out;
}

/** The last amount after a label on its line, else the first on the next line. */
function amountAfter(lines: readonly string[], label: RegExp, not?: RegExp): number | null {
  for (let i = 0; i < lines.length; i += 1) {
    const line = lines[i]!;
    const m = label.exec(line);
    if (!m || (not && not.test(line))) continue;
    const rest = line.slice(m.index + m[0].length);
    const here = moneyOnLine(rest);
    if (here.length > 0) return here[here.length - 1]!;
    const next = lines[i + 1] ? moneyOnLine(lines[i + 1]!) : [];
    if (next.length > 0 && rest.trim() === '') return next[0]!;
  }
  return null;
}

function dateAfter(lines: readonly string[], label: RegExp): string | null {
  for (let i = 0; i < lines.length; i += 1) {
    const m = label.exec(lines[i]!);
    if (!m) continue;
    const here = findInvoiceDate(lines[i]!.slice(m.index + m[0].length));
    if (here) return here;
    const next = lines[i + 1] ? findInvoiceDate(lines[i + 1]!) : null;
    if (next) return next;
  }
  return null;
}

/** What the text-layer reading found. */
export interface SupplierInvoiceTextReading {
  readonly values: SupplierInvoiceValues;
  /** Boxes filled from the text. */
  readonly found: readonly SupplierInvoiceField[];
  /** Boxes not found (the admin types them). */
  readonly missing: readonly SupplierInvoiceField[];
  /** Boxes filled but to check closely (a guessed supplier, amounts that do not add up). */
  readonly unsure: readonly SupplierInvoiceField[];
  /** The category code the supplier suggests, or null. */
  readonly category: string | null;
  /** False when the PDF has no text to read (a scan). */
  readonly hasText: boolean;
}

/**
 * The cost form's values from an invoice's text layer (spec §4.3), tried on genuine invoices of GitHub, Anthropic
 * (Stripe's invoice and receipt), Apple through PayPal and VentraIP (tests/fixtures/supplier-invoices). `excludeAbns`
 * are ABNs that are never the supplier's (Braynewave's own, printed as the customer's).
 */
export function readSupplierInvoiceText(
  lines: readonly string[],
  excludeAbns: readonly string[] = [],
): SupplierInvoiceTextReading {
  const clean = lines.map((l) => l.replace(/\s+/g, ' ').trim()).filter((l) => l !== '');
  const body = clean.join('\n');
  const unsure = new Set<SupplierInvoiceField>();
  const exclude = new Set(excludeAbns.map((a) => abnDigits(a)).filter((a): a is string => a !== null));

  // ABN: labelled first, then any 11 digits in the ABN's groups that pass the check
  let abn: string | null = null;
  const abnCandidates = [
    ...[...body.matchAll(/\bA\.?\s?B\.?\s?N\.?\s*(?:no\.?|number)?\s*:?\s*((?:\d[\s.]?){10}\d)\b/gi)].map(
      (m) => m[1]!,
    ),
    ...[...body.matchAll(/\b\d{2}\s\d{3}\s\d{3}\s\d{3}\b/g)].map((m) => m[0]),
  ];
  for (const c of abnCandidates) {
    const d = abnDigits(c);
    if (d && abnValid(d) && !exclude.has(d)) {
      abn = d;
      break;
    }
  }

  // invoice number: after "Invoice number", "Invoice no", "Invoice #", "Invoice ID", on its line or (the label
  // alone) the next; else a "#22375604" standing alone. It holds a digit and is not a date. Stripe's invoice prints
  // "FPNMMY22-0001" whose hyphen its text layer drops ("FPNMMY22 0001"): taken with the space, to check.
  let invoiceNumber: string | null = null;
  const NUMBER_LABEL = /\b(?:tax\s+)?(?:invoice|receipt)\s*(?:number|no\.?|num\.?|#|id)\s*:?\s*#?\s*/i;
  const TOKEN = /^([A-Za-z0-9][A-Za-z0-9./_-]{0,40}(?:\s\d{2,8}(?![\d./-]))?)/;
  const usable = (t: string | undefined): t is string => !!t && /\d/.test(t) && !findInvoiceDate(t);
  for (let i = 0; i < clean.length && !invoiceNumber; i += 1) {
    const m = NUMBER_LABEL.exec(clean[i]!);
    if (!m) continue;
    const rest = clean[i]!.slice(m.index + m[0].length);
    const here = TOKEN.exec(rest)?.[1];
    const next = TOKEN.exec(clean[i + 1] ?? '')?.[1];
    if (usable(here)) invoiceNumber = here;
    else if (rest.trim() === '' && usable(next)) invoiceNumber = next;
  }
  if (!invoiceNumber) {
    const m = /(?:^|\s)#\s?([A-Za-z0-9-]*\d[A-Za-z0-9-]*)\b/.exec(body);
    if (m) invoiceNumber = m[1]!;
  }
  if (invoiceNumber && /\s/.test(invoiceNumber)) unsure.add('invoice_number');

  let invoiceDate =
    dateAfter(
      clean,
      /\b(?:invoice|issue|tax invoice)\s*date\b|\bdate\s+(?:of\s+)?issued?\b|\bissued\s*(?:on)?\b/i,
    ) ?? dateAfter(clean, /^\s*date\b(?!\s*(?:due|paid))\s*:?/i);
  const dueDate = dateAfter(clean, /\b(?:due\s*date|date\s*due|payment\s*due|due\s*(?:on|by))\b/i);
  const paidOn = dateAfter(clean, /\b(?:date\s*paid|paid\s*on)\b/i);
  if (!invoiceDate) {
    // a receipt dated only by its payment, or a statement with one date: the first date, to check
    invoiceDate = paidOn ?? findInvoiceDate(body);
    if (invoiceDate) unsure.add('invoice_date');
  }

  // the total charged, before what is still owing ("Balance due $0.00" on a paid invoice is not its total)
  const total =
    amountAfter(clean, /\btotal\s*\(?\s*(?:incl?\.?|including)\s*(?:gst|tax)\)?/i) ??
    amountAfter(clean, /\b(?:grand\s+total|invoice\s+total)\b/i) ??
    amountAfter(
      clean,
      /^\s*total\b/i,
      /\b(sub-?total|excl?\.?|excluding|before|discount)\b|\btotal\s+(?:gst|tax)\b/i,
    ) ??
    amountAfter(
      clean,
      /\b(?:total\s+amount\s+due|amount\s+due|total\s+due|balance\s+due|amount\s+paid|amount\s+payable)\b/i,
    );
  let exGst =
    amountAfter(
      clean,
      /\btotal\s*\(?\s*(?:excl?\.?|excluding)\s*(?:gst|tax)\)?|\bamount\s+(?:excl?\.?|excluding)\s+(?:gst|tax)\b/i,
    ) ?? amountAfter(clean, /\bsub-?\s?total\b/i);
  const gst =
    amountAfter(clean, /\b(?:total\s+)?gst\b/i, /\b(?:incl?\.?|including|excl?\.?|excluding)\s*gst\b/i) ??
    amountAfter(
      clean,
      /^\s*(?:total\s+)?(?:tax|vat)\b/i,
      /\b(?:incl?\.?|including|excl?\.?|excluding)\s*(?:tax|vat)\b/i,
    );
  // a subtotal before a discount (VentraIP's) does not add up: the total and GST are what was charged
  if (exGst !== null && gst !== null && total !== null && cents(exGst) + cents(gst) !== cents(total)) {
    exGst = (cents(total) - cents(gst)) / 100;
    unsure.add('amount_ex_gst');
  }

  // currency: the code or marked dollar printed most
  let currency: string | null = null;
  const codes = [...body.matchAll(/\b(AUD|USD|NZD|EUR|GBP)\b|(A\$|AU\$|US\$|NZ\$)/g)].map(
    (m) => parseCurrency(m[1] ?? m[2] ?? '') ?? '',
  );
  if (codes.length > 0) {
    const count = (c: string): number => codes.filter((x) => x === c).length;
    currency = [...new Set(codes)].sort((a, b) => count(b) - count(a))[0] || null;
  }

  // supplier: the known name printed first (it heads its invoice; a payment method comes later), else the first line
  // that is not a heading, a date or an amount (to check)
  let supplier: string | null = null;
  let at = Infinity;
  for (const s of KNOWN_SUPPLIERS) {
    const m = s.name ? s.match.exec(body) : null;
    if (m && m.index < at) {
      at = m.index;
      supplier = s.name;
    }
  }
  if (!supplier) {
    const first = clean.find(
      (l) =>
        !/^(tax\s+)?(invoice|receipt|bill|statement)\b/i.test(l) &&
        !findInvoiceDate(l) &&
        /[A-Za-z]{3}/.test(l) &&
        moneyOnLine(l).length === 0 &&
        l.length <= 80,
    );
    if (first) {
      supplier = first;
      unsure.add('supplier');
    }
  }

  // description: the first item under the "Description" heading of the lines, without its quantity and prices
  let description: string | null = null;
  const head = clean.findIndex(
    (l) => /\bdescription\b/i.test(l) && /\b(qty|quantity|amount|total|rate|price)\b/i.test(l),
  );
  if (head >= 0) {
    for (const l of clean.slice(head + 1, head + 4)) {
      const words = l
        .replace(MONEY_RE, ' ')
        .replace(/\b\d+(?:\.\d+)?\s*%/g, ' ')
        .replace(/(\s\d+)+\s*$/, '')
        .replace(/\s+/g, ' ')
        .trim();
      if (/[A-Za-z]{3}/.test(words)) {
        description = words.slice(0, 500);
        unsure.add('description');
        break;
      }
    }
  }

  const { values, addsUp } = completeAmounts({
    supplier,
    supplier_abn: abn,
    invoice_number: invoiceNumber,
    invoice_date: invoiceDate,
    due_date: dueDate,
    paid_on: paidOn,
    description,
    currency,
    amount_ex_gst: exGst,
    gst,
    total,
  });
  if (!addsUp) unsure.add('total');
  const found = SUPPLIER_INVOICE_FIELDS.filter((k) => values[k] !== null);
  return {
    values,
    found,
    missing: SUPPLIER_INVOICE_FIELDS.filter((k) => values[k] === null),
    unsure: SUPPLIER_INVOICE_FIELDS.filter((k) => unsure.has(k)),
    category: suggestCategoryCode(values.supplier, body),
    hasText: clean.length > 0,
  };
}

/**
 * Claude's reading with the text layer's filling any box Claude left empty; Claude's unsure boxes stay, and a box
 * taken from the text layer is added to them.
 */
export function mergeReadings(
  claude: SupplierInvoiceReading,
  fromText: SupplierInvoiceTextReading,
): SupplierInvoiceReading {
  const unsure = new Set(claude.unsure);
  const merged: Record<string, unknown> = { ...claude.values };
  for (const k of SUPPLIER_INVOICE_FIELDS) {
    if (merged[k] === null && fromText.values[k] !== null) {
      merged[k] = fromText.values[k];
      unsure.add(k);
    }
  }
  const { values, addsUp } = completeAmounts(merged as unknown as SupplierInvoiceValues);
  if (!addsUp) unsure.add('total');
  return { values, unsure: SUPPLIER_INVOICE_FIELDS.filter((k) => unsure.has(k)) };
}
